// TikTok Mini Game SDK Types & Rewarded Video Ad Manager

export type SkillType = "super" | "bomb";

export type SlingshotSkinId = "classic" | "gold" | "cyber" | "magma";

export interface SlingshotSkinDef {
  id: SlingshotSkinId;
  name: string;
  tagline: string;
  unlockLevel: number;
  icon: string;
  themeColor: string;
  glowColor: string;
  bandOuter: string;
  bandMid: string;
  bandHighlight: string;
  pouchOuter: string;
  pouchGrad1: string;
  pouchGrad2: string;
  pouchBorder: string;
  grommetColor: string;
  trailColor: string;
  sparkColors: string[];
}

export const SLINGSHOT_SKINS: SlingshotSkinDef[] = [
  {
    id: "classic",
    name: "Kayu Rimba Klasik",
    tagline: "Kayu jati autentik rimba tropis dengan karet getah alami.",
    unlockLevel: 1,
    icon: "🪵",
    themeColor: "#82532f",
    glowColor: "rgba(130, 83, 47, 0.4)",
    bandOuter: "#562208",
    bandMid: "#cd621d",
    bandHighlight: "#ffb858",
    pouchOuter: "rgba(32, 16, 6, 0.85)",
    pouchGrad1: "#7c4420",
    pouchGrad2: "#3c1d0a",
    pouchBorder: "#deb887",
    grommetColor: "#e5b138",
    trailColor: "rgba(255, 235, 140, 0.6)",
    sparkColors: ["#ffe57f", "#d7aa6a", "#fff9c4"],
  },
  {
    id: "gold",
    name: "Bambu Emas Kerajaan",
    tagline: "Bambu suci bersepuh emas murni dengan kilau bintang kemewahan.",
    unlockLevel: 3,
    icon: "✨",
    themeColor: "#ffd700",
    glowColor: "rgba(255, 215, 0, 0.55)",
    bandOuter: "#996515",
    bandMid: "#e6b800",
    bandHighlight: "#fff275",
    pouchOuter: "rgba(50, 40, 0, 0.85)",
    pouchGrad1: "#d4af37",
    pouchGrad2: "#8a6d1a",
    pouchBorder: "#fff8dc",
    grommetColor: "#ff4500",
    trailColor: "rgba(255, 223, 0, 0.85)",
    sparkColors: ["#ffd700", "#fff275", "#ffffff"],
  },
  {
    id: "cyber",
    name: "Cyber Neon Mecha",
    tagline: "Rangka karbon teknologi tinggi bertenaga plasma neon masa depan.",
    unlockLevel: 6,
    icon: "⚡",
    themeColor: "#00f0ff",
    glowColor: "rgba(0, 240, 255, 0.6)",
    bandOuter: "#003b46",
    bandMid: "#00b4d8",
    bandHighlight: "#90e0ef",
    pouchOuter: "rgba(10, 25, 47, 0.9)",
    pouchGrad1: "#073b4c",
    pouchGrad2: "#021820",
    pouchBorder: "#00f0ff",
    grommetColor: "#ff007f",
    trailColor: "rgba(0, 240, 255, 0.85)",
    sparkColors: ["#00f0ff", "#ff007f", "#70e000"],
  },
  {
    id: "magma",
    name: "Magma Vulkanik Api",
    tagline: "Batu obsidian lahar gunung berapi dengan semburan api berkobar.",
    unlockLevel: 9,
    icon: "🔥",
    themeColor: "#ff3b00",
    glowColor: "rgba(255, 59, 0, 0.6)",
    bandOuter: "#570000",
    bandMid: "#d00000",
    bandHighlight: "#ffba08",
    pouchOuter: "rgba(35, 5, 5, 0.9)",
    pouchGrad1: "#6a040f",
    pouchGrad2: "#2b0000",
    pouchBorder: "#ff6d00",
    grommetColor: "#ffdd00",
    trailColor: "rgba(255, 80, 0, 0.85)",
    sparkColors: ["#ff3b00", "#ffba08", "#ffffff"],
  },
];

const STORAGE_KEYS = {
  SKILLS_PREFIX: "monkey_skill_",
  SKINS_PREFIX: "monkey_skin_unlocked_",
  ACTIVE_SKIN: "monkey_active_skin",
  HIGH_SCORE: "monkey_high_score",
  MAX_UNLOCKED_LEVEL: "monkey_unlocked_level",
};

export function getStoredSkillAmmo(type: SkillType): number {
  if (typeof window === "undefined") return 1;
  const val = localStorage.getItem(STORAGE_KEYS.SKILLS_PREFIX + type);
  return val !== null ? parseInt(val, 10) : 1; // Start with 1 free charge each
}

export function setStoredSkillAmmo(type: SkillType, amount: number): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.SKILLS_PREFIX + type, String(Math.max(0, amount)));
}

export function isSkinUnlocked(skinId: SlingshotSkinId, currentMaxLevel: number): boolean {
  if (skinId === "classic") return true;
  if (typeof window === "undefined") return false;
  if (localStorage.getItem(STORAGE_KEYS.SKINS_PREFIX + skinId) === "true") return true;
  const skin = SLINGSHOT_SKINS.find((s) => s.id === skinId);
  return Boolean(skin && currentMaxLevel >= skin.unlockLevel);
}

export function setSkinUnlocked(skinId: SlingshotSkinId): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.SKINS_PREFIX + skinId, "true");
}

export function getActiveSkin(): SlingshotSkinId {
  if (typeof window === "undefined") return "classic";
  const skin = localStorage.getItem(STORAGE_KEYS.ACTIVE_SKIN) as SlingshotSkinId | null;
  return skin && SLINGSHOT_SKINS.some((s) => s.id === skin) ? skin : "classic";
}

export function setActiveSkin(skinId: SlingshotSkinId): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.ACTIVE_SKIN, skinId);
}

export function getHighScore(): number {
  if (typeof window === "undefined") return 0;
  const s = localStorage.getItem(STORAGE_KEYS.HIGH_SCORE);
  return s ? parseInt(s, 10) : 0;
}

export function setHighScore(score: number): void {
  if (typeof window === "undefined") return;
  const cur = getHighScore();
  if (score > cur) {
    localStorage.setItem(STORAGE_KEYS.HIGH_SCORE, String(score));
  }
}

export function getMaxUnlockedLevel(): number {
  if (typeof window === "undefined") return 1;
  const lvl = localStorage.getItem(STORAGE_KEYS.MAX_UNLOCKED_LEVEL);
  return lvl ? Math.max(1, Math.min(20, parseInt(lvl, 10))) : 1;
}

export function setMaxUnlockedLevel(level: number): void {
  if (typeof window === "undefined") return;
  const cur = getMaxUnlockedLevel();
  if (level > cur) {
    localStorage.setItem(STORAGE_KEYS.MAX_UNLOCKED_LEVEL, String(Math.min(20, level)));
  }
}

// ByteDance / TikTok Mini Game SDK declaration
interface TikTokRewardedVideoAd {
  show: () => Promise<void>;
  load: () => Promise<void>;
  onClose: (cb: (res: { isEnded: boolean }) => void) => void;
  onError: (cb: (err: unknown) => void) => void;
}

declare global {
  interface Window {
    tt?: {
      createRewardedVideoAd?: (params: { adUnitId: string }) => TikTokRewardedVideoAd;
    };
  }
}

export interface AdRequestOptions {
  rewardType: "super" | "bomb" | "skin";
  skinId?: SlingshotSkinId;
  adUnitId?: string;
  onProgress?: (secondsLeft: number) => void;
}

export async function showTikTokRewardedVideoAd(options: AdRequestOptions): Promise<boolean> {
  if (typeof window !== "undefined" && window.tt?.createRewardedVideoAd) {
    return new Promise<boolean>((resolve) => {
      try {
        const adUnitId = options.adUnitId || "adunit_tiktok_minigame_reward_01";
        const videoAd = window.tt!.createRewardedVideoAd!({ adUnitId });

        videoAd.onClose((res) => {
          if (res && res.isEnded) {
            resolve(true);
          } else {
            resolve(false);
          }
        });

        videoAd.onError((err) => {
          console.warn("[TikTok Ad SDK] Error loading native ad, falling back to simulated:", err);
          simulateBrowserAd(options).then(resolve);
        });

        videoAd
          .load()
          .then(() => videoAd.show())
          .catch(() => {
            simulateBrowserAd(options).then(resolve);
          });
      } catch (err) {
        console.warn("[TikTok Ad SDK] Exception invoking video ad:", err);
        simulateBrowserAd(options).then(resolve);
      }
    });
  }

  return simulateBrowserAd(options);
}

function simulateBrowserAd(options: AdRequestOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let remaining = 3;
    options.onProgress?.(remaining);
    const interval = setInterval(() => {
      remaining--;
      options.onProgress?.(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        resolve(true);
      }
    }, 1000);
  });
}
